import {
  AiSlimBanner,
  Button,
  Css,
  HelperText,
  IconButton,
  ModalBanner,
  ModalBody,
  ModalFooter,
  ModalHeader,
  SelectField,
  Tag,
  useComputed,
  useModal,
} from "@homebound/beam";
import { FieldState, ObjectConfig, ObjectState, useFormState } from "@homebound/form-state";
import { Observer, observer } from "mobx-react";
import { ReactNode, useMemo } from "react";
import { BoundAiSelectField } from "src/components/formFields/ai";
import { AiFieldSources, pendingSources } from "src/components/formFields/ai/AiProposalValue";
import {
  aiProposalsConfig,
  type AiProposalsFormValue,
  flattenEntityProposals,
  toResolveInputs,
} from "src/components/formFields/ai/utils/aiProposalForm";
import { type AiFormAepFragment } from "src/components/formFields/ai/utils/aiProposalForm.generated";
import { parseAiNamedOption, parseAiNumber } from "src/components/formFields/ai/utils/bindAiProposal";
import {
  type AiFieldBinding,
  aiFieldBinding,
  reconcilePendingAiProposals,
  resolvePendingAiProposals,
} from "src/components/formFields/ai/utils/reconcilePendingAiProposals";
import { AiEntityProposalType, AiFieldProposalStatus, Maybe, SavePlanPackageInput } from "src/generated/graphqlTypes";
import { useNavigateToRpav } from "src/hooks/useNavigateToRpav";
import { disableBasedOnPotentialOperation } from "src/routes/components/PotentialOperationsUtils";
import {
  PlanPackageOptionsV2_OptionFragment,
  PlanPackageOptionsV2_PlanPackageFragment,
  PlanPackageOptionsV2Query,
  useSavePlanPackageOptionsV2Mutation,
} from "src/routes/libraries/plan-package-v2/components/OptionsTab.generated";
import {
  ImpactRow,
  impactsConfig,
  optionSqftFields,
  ProgramDataImpactsSection,
  toImpactRows,
  toProgramDataInputs,
} from "./ProgramDataImpactsSection";

type EnumDetails = PlanPackageOptionsV2Query["enumDetails"];

type EditOptionModalProps = {
  planPackage: PlanPackageOptionsV2_PlanPackageFragment;
  option: PlanPackageOptionsV2_OptionFragment;
  enumDetails: EnumDetails;
  aep?: AiFormAepFragment;
};

type EditOptionForm = AiProposalsFormValue & {
  readyPlanLocationId: Maybe<string>;
  optionPrerequisiteIds: string[];
  /** The combined conflicts, i.e. `optionConflicts` and `optionConflictChildren`, since they are bidirectional. */
  conflictIds: string[];
  impacts: ImpactRow[];
};
type FormState = ObjectState<EditOptionForm>;

export function EditOptionModal(props: EditOptionModalProps) {
  const { planPackage, option, enumDetails, aep } = props;
  const { closeModal } = useModal();
  const navigateToRpav = useNavigateToRpav();
  const [save] = useSavePlanPackageOptionsV2Mutation();
  const isCreate = aep?.type.code === AiEntityProposalType.Create;
  const isRemoval = aep?.type.code === AiEntityProposalType.Delete;
  const formState = useFormState({
    config: formConfig,
    init: { input: option, map: (o) => mapToForm(o, planPackage, aep) },
  });
  // TODO: Check with design if they want to allow accepting/rejecting a deletion from w/in the modal as well.
  // A proposed removal is resolved from the table, shepherd users back to table to handle rejecting it.
  const disabled =
    disableBasedOnPotentialOperation(planPackage.canEdit) ||
    (isRemoval && "Ignore this option's removal via the table to allow editing");
  /** Only the square-footage impacts are reviewable here, so they alone drive the banner and Save. */
  const pendingSqft = useComputed(
    () => sqftBindings(formState).filter((b) => b.proposal?.status.value === AiFieldProposalStatus.Pending).length,
    [formState],
  );
  // A new option always resolves the proposals it was created from, even with no square footage to review.
  const savesProposals = isCreate || pendingSqft > 0;
  // The pages every pending proposal cites, when they are all the same, so the modal cites them once. The import
  // rates an option as a whole, so its proposals normally all repeat the same pages.
  const sharedSources = useComputed(() => {
    const sources = formState.fieldProposals.rows.map(pendingSources).compact();
    return sources.map((s) => s.pageNumbers.toSorted((a, b) => a - b).join()).unique().length === 1
      ? sources.first
      : undefined;
  }, [formState]);

  async function onSave() {
    // Save commits what the AI fields are showing, so anything left pending is an accept.
    reconcilePendingAiProposals(sqftBindings(formState), AiFieldProposalStatus.Accepted);
    // Accepting a new option accepts the identity it was created with. A field the user overrode has
    // already resolved itself on edit, so only what is still pending is an accept.
    if (isCreate) resolvePendingAiProposals(formState.fieldProposals.rows, AiFieldProposalStatus.Accepted);
    const input = toSaveInput(formState, option, planPackage);
    if (input.options || input.programDatas || input.proposals) {
      const { data } = await save({ variables: { input } });
      const versionId = data?.savePlanPackage.planPackage.version.id;
      // Saving on a published version forks a new draft, so navigate to it
      if (versionId && versionId !== planPackage.version.id) navigateToRpav(versionId);
    }
    closeModal();
  }

  return (
    <>
      <ModalHeader>
        <div css={Css.xs.gray700.$}>{option.code}</div>
        <div css={Css.xl.pt1.$}>{option.name}</div>
        <Tag text={option.type.name} />
      </ModalHeader>
      {aep && (
        <OptionProposalBanner
          isCreate={isCreate}
          isRemoval={isRemoval}
          pending={pendingSqft}
          onIgnore={() => reconcilePendingAiProposals(sqftBindings(formState), AiFieldProposalStatus.Rejected)}
        />
      )}
      <Observer>
        {() => (
          <>
            <ModalBody>
              <div css={Css.df.fdc.gap3.pt2.pb6.$}>
                {option.description && <div>{option.description}</div>}
                {option.type.allowsLocations && (
                  <LocationSection
                    planPackage={planPackage}
                    option={option}
                    formState={formState}
                    disabled={disabled}
                    hideAiSources={!!sharedSources}
                  />
                )}
                <RelatedOptionsSection
                  title="Requirements"
                  subtitle="Add an option that must be selected first before this option can be selected."
                  placeholder="Add a Requirement"
                  field={formState.optionPrerequisiteIds}
                  options={eligiblePrerequisites(option, planPackage.options, formState)}
                  allOptions={planPackage.options}
                  disabled={disabled}
                />
                <RelatedOptionsSection
                  title="Conflicts"
                  subtitle="Add an option that cannot be combined with this option."
                  placeholder="Add a Conflict"
                  field={formState.conflictIds}
                  options={eligibleConflicts(option, planPackage.options, formState)}
                  allOptions={planPackage.options}
                  disabled={disabled}
                />
                <div>
                  <SectionHeader
                    title="Program Data Impact"
                    subtitle="Indicate how this option changes program data like square footage or bed/bath count."
                  />
                  <ProgramDataImpactsSection
                    option={option}
                    planPackage={planPackage}
                    enumDetails={enumDetails}
                    impacts={formState.impacts}
                    fieldProposals={formState.fieldProposals.rows}
                    disabled={disabled}
                    hideAiSources={!!sharedSources}
                  />
                </div>
                {sharedSources && <HelperText helperText={<AiFieldSources {...sharedSources} />} />}
              </div>
            </ModalBody>
            <ModalFooter>
              <Button label={isRemoval ? "Close" : "Cancel"} variant="tertiary" onClick={closeModal} />
              {!isRemoval && (
                <Button
                  label={savesProposals ? "Save AI Changes" : "Save"}
                  variant={savesProposals ? "ai" : "primary"}
                  onClick={onSave}
                  disabled={disabled || (!formState.dirty && pendingSqft === 0 && !isCreate)}
                />
              )}
            </ModalFooter>
          </>
        )}
      </Observer>
    </>
  );
}

/**
 * What the import did to this option, and the one review action the modal offers.
 *
 * A new or removed option is a statement about the whole option, so neither shows a count. An
 * update only has square footage to review, so the banner goes away once that is decided.
 */
function OptionProposalBanner(props: { isCreate: boolean; isRemoval: boolean; pending: number; onIgnore: () => void }) {
  const { isCreate, isRemoval, pending, onIgnore } = props;
  const title = isCreate
    ? "New Option, added from your latest document capture"
    : isRemoval
      ? "This option was removed in your latest document capture"
      : pending > 0 && `Review ${pending} Suggested Change${pending === 1 ? "" : "s"}`;
  if (!title) return null;
  return (
    <ModalBanner>
      <AiSlimBanner
        title={title}
        action={
          isCreate || isRemoval ? undefined : { label: pending === 1 ? "Ignore" : "Ignore All", onClick: onIgnore }
        }
      />
    </ModalBanner>
  );
}

/** The impact rows the import proposed a value for, paired with how to coerce it. */
function sqftBindings(formState: FormState): AiFieldBinding[] {
  return formState.impacts.rows.flatMap((row) => {
    const proposal = formState.fieldProposals.rows.find((p) => p.fieldName.value === row.fieldKey.value);
    return proposal ? [aiFieldBinding(row.impact, parseAiNumber, proposal)] : [];
  });
}

function LocationSection(props: {
  planPackage: PlanPackageOptionsV2_PlanPackageFragment;
  option: PlanPackageOptionsV2_OptionFragment;
  formState: FormState;
  disabled: ReactNode | boolean;
  /** When every pending proposal cites the same pages, so the modal cites them once at the bottom instead. */
  hideAiSources: boolean;
}) {
  const { planPackage, option, formState, disabled, hideAiSources } = props;
  // Moving an option also moves its whole group, so it is only allowed for single-option groups
  const groupSiblings = planPackage.options.filter(
    (o) => o.id !== option.id && o.optionGroup.id === option.optionGroup.id,
  );
  const sameOptionSiblings = planPackage.options.filter(
    (o) => o.id !== option.id && o.globalOption.id === option.globalOption.id,
  );
  // `location` is proposed on a CREATE only — a relocated option comes back as a new option, never an update.
  const aiProposal = formState.fieldProposals.rows.find((p) => p.fieldName.value === "location");
  // The proposal names the location, the field holds its id.
  const parser = useMemo(() => parseAiNamedOption(planPackage.roomLocations), [planPackage.roomLocations]);
  return (
    <BoundAiSelectField
      label="Location"
      field={formState.readyPlanLocationId}
      aiProposal={aiProposal}
      hideAiSources={hideAiSources}
      parser={parser}
      options={planPackage.roomLocations}
      unsetLabel="None"
      fullWidth
      disabled={
        disabled || (groupSiblings.nonEmpty && "Options that share an option group must stay in the same location")
      }
      disabledOptions={sameOptionSiblings
        .map((o) => o.readyPlanLocation?.id)
        .compact()
        .map((value) => ({ value, reason: `Another ${option.name} is already assigned to this location` }))}
    />
  );
}

type RelatedOptionsSectionProps = {
  title: string;
  subtitle: string;
  placeholder: string;
  field: FieldState<string[]>;
  options: PlanPackageOptionsV2_OptionFragment[];
  allOptions: PlanPackageOptionsV2_OptionFragment[];
  disabled: ReactNode | boolean;
};

/** A list of related options (requirements or conflicts) as removable rows plus an "add" select. */
const RelatedOptionsSection = observer(function RelatedOptionsSection(props: RelatedOptionsSectionProps) {
  const { title, subtitle, placeholder, field, options, allOptions, disabled } = props;
  const selected = (field.value ?? []).map((id) => allOptions.find((o) => o.id === id)).compact();
  return (
    <div>
      <SectionHeader title={title} subtitle={subtitle} />
      <div css={Css.df.fdc.gap1.$}>
        {selected.map((o) => (
          <div key={o.id} css={Css.df.aic.gap1.ba.bcGray300.br4.pxPx(12).pyPx(6).$}>
            <span css={Css.xs.gray700.$}>{o.code}</span>
            <span css={Css.smSb.$}>{o.name}</span>
            <span css={Css.xs.gray700.mla.$}>{o.readyPlanLocation?.name ?? "None"}</span>
            <IconButton
              icon="x"
              disabled={disabled}
              onClick={() => field.set(field.value!.filter((id) => id !== o.id))}
            />
          </div>
        ))}
        <SelectField
          label={title}
          fullWidth
          labelStyle="hidden"
          placeholder={placeholder}
          value={undefined as string | undefined}
          options={options}
          getOptionLabel={(o) => `${o.code} - ${o.name}${o.readyPlanLocation ? ` (${o.readyPlanLocation.name})` : ""}`}
          getOptionValue={(o) => o.id}
          onSelect={(id) => id && field.set([...(field.value ?? []), id])}
          disabled={disabled}
        />
      </div>
    </div>
  );
});

function SectionHeader({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <div css={Css.pb1.$}>
      <h2 css={Css.mdSb.$}>{title}</h2>
      <div css={Css.sm.gray700.pt1.$}>{subtitle}</div>
    </div>
  );
}

const formConfig: ObjectConfig<EditOptionForm> = {
  ...aiProposalsConfig,
  readyPlanLocationId: { type: "value" },
  optionPrerequisiteIds: { type: "value" },
  conflictIds: { type: "value" },
  impacts: { type: "list", config: impactsConfig },
};

function mapToForm(
  option: PlanPackageOptionsV2_OptionFragment,
  planPackage: PlanPackageOptionsV2_PlanPackageFragment,
  aep: Maybe<AiFormAepFragment>,
): EditOptionForm {
  // A proposed square footage the option does not impact yet still needs a row to render on. Only
  // pending ones: a resolved proposal has nothing left to show, so an empty row would just confuse.
  const proposedFieldKeys = optionSqftFields.filter((fieldName) =>
    aep?.fieldProposals.some((fp) => fp.fieldName === fieldName && fp.status.code === AiFieldProposalStatus.Pending),
  );
  return {
    readyPlanLocationId: option.readyPlanLocation?.id,
    optionPrerequisiteIds: option.optionPrerequisites.map((o) => o.id),
    conflictIds: [...option.optionConflicts, ...option.optionConflictChildren].uniqueBy((o) => o.id).map((o) => o.id),
    impacts: toImpactRows(option, planPackage, proposedFieldKeys),
    fieldProposals: flattenEntityProposals(aep ? [aep] : []),
  };
}

function toSaveInput(
  formState: FormState,
  option: PlanPackageOptionsV2_OptionFragment,
  planPackage: PlanPackageOptionsV2_PlanPackageFragment,
): SavePlanPackageInput {
  const { readyPlanLocationId, optionPrerequisiteIds, conflictIds, impacts } = formState;
  const optionInput = {
    id: option.id,
    ...(readyPlanLocationId.dirty && { readyPlanLocationId: readyPlanLocationId.value ?? null }),
    ...(optionPrerequisiteIds.dirty && { optionPrerequisiteIds: optionPrerequisiteIds.value }),
    // Conflicts are bidirectional but stored one-way, so removals must apply to whichever side holds the edge,
    // while additions always become our outgoing conflicts
    ...(conflictIds.dirty && {
      optionConflictIds: conflictIds.value.filter((id) => !option.optionConflictChildren.some((o) => o.id === id)),
      optionConflictChildIds: option.optionConflictChildren
        .map((o) => o.id)
        .filter((id) => conflictIds.value.includes(id)),
    }),
  };
  const programDatas = impacts.dirty ? toProgramDataInputs(option, planPackage, formState.impacts.value) : [];
  const proposals = toResolveInputs(formState.fieldProposals.changedValue);
  return {
    id: planPackage.id,
    ...(Object.keys(optionInput).length > 1 && { options: [optionInput] }),
    ...(programDatas.nonEmpty && { programDatas }),
    ...(proposals.nonEmpty && { proposals }),
  };
}

/** Options this option could require */
function eligiblePrerequisites(
  option: PlanPackageOptionsV2_OptionFragment,
  allOptions: PlanPackageOptionsV2_OptionFragment[],
  formState: FormState,
) {
  const conflictIds = formState.conflictIds.value ?? [];
  const prereqIds = formState.optionPrerequisiteIds.value ?? [];
  return (
    allOptions
      .filter((o) => o.id !== option.id)
      .filter((o) => o.optionGroup.id !== option.optionGroup.id)
      .filter((o) => !prereqIds.includes(o.id))
      .filter((o) => !conflictIds.includes(o.id))
      // Avoid cycles, i.e. the candidate cannot (transitively) require this option
      .filter((o) => !transitivePrerequisiteIds(o, allOptions).includes(option.id))
      .sortBy((o) => o.name)
  );
}

/** Options this option could conflict with. */
function eligibleConflicts(
  option: PlanPackageOptionsV2_OptionFragment,
  allOptions: PlanPackageOptionsV2_OptionFragment[],
  formState: FormState,
) {
  const conflictIds = formState.conflictIds.value ?? [];
  const prereqIds = formState.optionPrerequisiteIds.value ?? [];
  return allOptions
    .filter((o) => o.id !== option.id)
    .filter((o) => o.optionGroup.id !== option.optionGroup.id)
    .filter((o) => !conflictIds.includes(o.id))
    .filter((o) => !prereqIds.includes(o.id))
    .sortBy((o) => o.name);
}

function transitivePrerequisiteIds(
  option: PlanPackageOptionsV2_OptionFragment,
  allOptions: PlanPackageOptionsV2_OptionFragment[],
  visited: Set<string> = new Set(),
): string[] {
  return option.optionPrerequisites.flatMap(({ id }) => {
    if (visited.has(id)) return [];
    visited.add(id);
    const prereq = allOptions.find((o) => o.id === id);
    return [id, ...(prereq ? transitivePrerequisiteIds(prereq, allOptions, visited) : [])];
  });
}
